<template>
    <Teleport to="body">
        <Transition name="fade">
            <dialog
                ref="dialogEL"
                class="teaser-overlay-dialog overflow-x-hidden overflow-y-auto bg-white backdrop:bg-black/50"
                :style="overlayDialogTopMarginContent.style"
                :class="overlayDialogTopMarginContent.class"
                @click="handleDialogClick">
                <div ref="embedded" />
            </dialog>
        </Transition>
    </Teleport>
</template>

<script lang="ts">
import { createVNode, nextTick, render } from 'vue';
import { gsap } from 'gsap';
import { mapState } from 'pinia';
import debounce from 'lodash/debounce';
import { TopMarginResult, useNavigationStore } from '../../stores/navigation';

export default {
    props: {
        url: { type: String },
    },
    emits: ['close'],
    data() {
        return {
            el: null,
            debouncedUpdateOverlayDialogHeight: null as null | ReturnType<typeof debounce>,
            overlayDialogListenersActive: false,
        };
    },
    computed: {
        ...mapState(useNavigationStore, ['additionalTopMarginContent']),
        overlayDialogTopMarginContent(): TopMarginResult {
            return this.additionalTopMarginContent({ prefix: 'top' });
        },
        overlayDialogTopMarginSignature(): string {
            return `${this.overlayDialogTopMarginContent.class}|${JSON.stringify(this.overlayDialogTopMarginContent.style)}`;
        },
    },
    watch: {
        async url() {
            if (this.url.length > 0) {
                await this.show();
            } else {
                this.hide();
            }
        },
        overlayDialogTopMarginSignature() {
            if (this.$refs.dialogEL?.open) {
                this.updateOverlayDialogHeight();
            }
        },
    },
    beforeUnmount() {
        this.removeOverlayDialogListeners();
    },
    mounted() {
        // Reuse single debounced resize handler while overlay is open.
        this.debouncedUpdateOverlayDialogHeight = debounce(() => this.updateOverlayDialogHeight(), 300);
    },
    methods: {
        /**
         * Closes the native modal only when the click lands on the backdrop area.
         */
        handleDialogClick(event: MouseEvent) {
            if (event.target !== this.$refs.dialogEL) {
                return;
            }

            const rect = this.$refs.dialogEL.getBoundingClientRect();
            const isBackdropClick =
                event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;

            if (isBackdropClick) {
                this.hide();
            }
        },

        /**
         * Actually fetches the dialog content.
         */
        fetchContent(): Promise<Element> {
            return new Promise((resolve, reject) => {
                // eslint-disable-next-line no-undef
                // Below parameter 'embedded' is checked for in 'gc/templates/pages/overlay.ftl'
                fetch(this.url + '?embedded=true')
                    .then(res => res.text())
                    .then(html => {
                        // insert the fetched html into the dom

                        const mount = {
                            props: [],
                            template: html,
                        };
                        // https://stackoverflow.com/questions/76774836/vue-3-equivalent-of-vue-extend-mount
                        const vm = createVNode(mount);
                        vm.appContext = this._.appContext;
                        render(vm, this.$refs.embedded);

                        nextTick(() => {
                            // get the inserted html as element
                            resolve(this.$el);
                        });
                    })
                    .catch(reject);
            });
        },
        async show() {
            try {
                await this.fetchContent();

                const tl: gsap.core.Timeline = gsap.timeline({ paused: true });
                tl.fromTo('.js-content-modal-bg', { height: 0 }, { height: '100%', duration: 1.0 }, 0.0);
                tl.fromTo('.js-content-modal-content', { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.4 }, 0.3);
                tl.fromTo('.js-content-modal-copy', { opacity: 0, y: -20 }, { opacity: 1, y: 0, duration: 0.5 }, 0.6);
                tl.restart();

                document.body.style.overflow = 'hidden';

                // get targets for open/close event
                const closeBtn: HTMLButtonElement = this.$refs.embedded.querySelector('.js-close-button');
                // and add the listeners
                closeBtn.addEventListener('click', this.hide);
                closeBtn.focus();
                // make sure hide method is called when dialog is closed by pressing escape
                this.$refs.dialogEL.addEventListener('close', this.hide);

                if (this.$refs.dialogEL && !this.$refs.dialogEL.open) {
                    this.$refs.dialogEL.showModal();
                    this.updateOverlayDialogHeight();
                    this.addOverlayDialogListeners();
                }
            } catch (err) {
                console.error(err);
            }
        },
        addOverlayDialogListeners() {
            if (this.overlayDialogListenersActive || !this.debouncedUpdateOverlayDialogHeight) {
                return;
            }

            window.addEventListener('resize', this.debouncedUpdateOverlayDialogHeight);
            this.overlayDialogListenersActive = true;
        },
        removeOverlayDialogListeners() {
            if (!this.overlayDialogListenersActive) {
                return;
            }

            window.removeEventListener('resize', this.debouncedUpdateOverlayDialogHeight);
            this.overlayDialogListenersActive = false;
        },
        /**
         * Waits for Vue + browser layout updates before measuring the rendered top offset.
         * This keeps the native dialog height in sync with navigation-driven top changes.
         */
        async updateOverlayDialogHeight() {
            await nextTick();
            window.requestAnimationFrame(() => {
                this.setOverlayDialogHeight();
            });
        },
        setOverlayDialogHeight() {
            const dialog = this.$refs.dialogEL as HTMLDialogElement;
            if (!dialog?.open) {
                return;
            }

            const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
            const top = dialog.getBoundingClientRect().top;
            dialog.style.height = `${Math.max(viewportHeight - top, 0)}px`;
        },
        hide() {
            this.$emit('close');
            document.body.style.overflow = '';
            this.removeOverlayDialogListeners();

            this.$refs.dialogEL.removeEventListener('close', this.hide);

            if (this.$refs.dialogEL.open) {
                this.$refs.dialogEL.close();
            }

            window.requestAnimationFrame(() => {
                // Timeout matches the animation time for it to fade.
                setTimeout(() => {
                    this.$refs.embedded.innerHTML = '';
                    this.$refs.dialogEL.style.height = '';
                }, 300);
            });
        },
    },
};
</script>
